import type { KyInstance } from "ky";
import { toSearchParams, type PaginatedRequest, type PaginatedResponse } from "./pagination.js";
import type { AuthProvider, UserRole } from "./index.js";

export type AdminUserSortKey = "name" | "email" | "role" | "lastLoginAt";

/** Whether a staff account may still sign in. Mirrors the `status` CHECK on `users`. */
export type AccountStatus = "active" | "revoked";

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  status: AccountStatus;
  lastLoginAt: string | null;
}

export interface ListAdminUsersRequest extends PaginatedRequest {
  sortBy?: AdminUserSortKey;
  role?: UserRole;
}

/** Wire shape of `/users/@me`, both GET and PATCH. */
export interface AdminMeDto {
  id: string;
  email: string;
  role: UserRole;
  status: AccountStatus;
  isEmailVerified: boolean;
  identities: AuthProvider[];
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
}

export interface UpdateMeRequest {
  firstName: string;
  lastName: string;
}

export class UserApi {
  constructor(private readonly http: KyInstance) { }

  list = (params: ListAdminUsersRequest = {}): Promise<PaginatedResponse<AdminUserListItem>> =>
    this.http
      .get("users", { searchParams: toSearchParams({ ...params }) })
      .json<PaginatedResponse<AdminUserListItem>>();

  me = (): Promise<AdminMeDto> => this.http.get("users/@me").json<AdminMeDto>();

  updateMe = (patch: UpdateMeRequest): Promise<AdminMeDto> =>
    this.http.patch("users/@me", { json: patch }).json<AdminMeDto>();

  /** Owner-only. Revoking ends the target's live sessions on their next request. */
  revoke = (id: string): Promise<AdminMeDto> =>
    this.http.post(`users/${encodeURIComponent(id)}/revoke`).json<AdminMeDto>();

  restore = (id: string): Promise<AdminMeDto> =>
    this.http.post(`users/${encodeURIComponent(id)}/restore`).json<AdminMeDto>();
}
